import { Injectable, inject, NgZone, signal } from "@angular/core";
import {
	Firestore,
	collection,
	doc,
	getDocs,
	getDoc,
	updateDoc,
	query,
	orderBy,
	where,
	setDoc,
	Timestamp,
	writeBatch,
	serverTimestamp,
	onSnapshot,
} from "@angular/fire/firestore";
import {
	Organization,
	CreateOrganizationData,
	UpdateOrganizationData,
} from "../models/organization.model";
import { OrganizationMembers } from "../models/associations.model";
import {
	BehaviorSubject,
	Observable,
	from,
	map,
	of,
	switchMap,
	firstValueFrom,
	throwError,
	catchError,
	combineLatest,
} from "rxjs";
import { CacheService, buildCacheKey } from "./cache.service";
import { Auth } from "@angular/fire/auth";
import { AuthService } from "./auth.service";
import { Survey } from "../models/survey.model";
import { fetchDocsByIds } from "../shared/utils/docs-by-id.util";

import { UserWithRoles, UserRoles } from "../models/user-role.model";
import { UserService } from "./user.service";
import { deriveDisplayName } from "../shared/utils/display-name.util";
import { normalizeEmail } from "../shared/utils/email.util";

@Injectable({
	providedIn: "root",
})
export class OrganizationsService {
	private readonly collectionName = "organizations";
	private firestore: Firestore = inject(Firestore);
	private auth: Auth = inject(Auth);
	private zone: NgZone = inject(NgZone);
	private authService: AuthService = inject(AuthService);
	private userService: UserService = inject(UserService);
	private cacheService = inject(CacheService);

	/**
	 * Normalizes survey data to handle the name/title field inconsistency.
	 *
	 * `name || title` is a genuine mapping between two spellings of the same
	 * field. There is deliberately no third fallback: a survey with neither is a
	 * data problem, and naming it "Untitled Survey" made that look intentional.
	 */
	private normalizeSurvey(survey: any): Survey {
		const displayName = survey.name || survey.title;
		return {
			...survey,
			displayName,
		} as Survey;
	}

	// Current organization context
	private currentOrganizationSubject = new BehaviorSubject<Organization | null>(null);
	public currentOrganization$ = this.currentOrganizationSubject.asObservable();

	constructor() {
		// Try to load the current organization from localStorage on init
		void this.loadCurrentOrganizationFromStorage();
	}

	// Get all organizations
	getOrganizations(): Observable<Organization[]> {
		const cacheKey = buildCacheKey("organizations", "all-active");

		return this.cacheService.get(
			cacheKey,
			() => {
				const organizationsRef = collection(this.firestore, this.collectionName);
				const organizationsQuery = query(
					organizationsRef,
					where("active", "==", true),
					orderBy("name"),
				);

				return new Observable<Organization[]>((subscriber) => {
					const unsubscribe = onSnapshot(
						organizationsQuery,
						(snapshot) => {
							const organizations = snapshot.docs.map((doc) => {
								const data = doc.data();
								// Map fields to ensure consistency
								return {
									id: doc.id,
									name: data["name"],
									description: data["description"],
									address: data["address"],
									city: data["city"],
									state: data["state"],
									country: data["country"],
									postal_code: data["postal_code"],
									phone: data["phone"],
									email: data["email"],
									website: data["website"],
									logo_url: data["logo_url"],
									active: true, // Active organizations only
									created_at: data["created_at"],
									updated_at: data["updated_at"],
									status: data["status"],
									display_name: data["display_name"],
									vat_number: data["vat_number"],
									industry: data["industry"],
								} as Organization;
							});
							subscriber.next(organizations);
						},
						(error) => {
							subscriber.error(error);
						},
					);

					// Return cleanup function
					return () => unsubscribe();
				});
			},
			{ duration: 5 * 60 * 1000 }, // Cache for 5 minutes
		);
	}

	// Get all organizations regardless of active status
	getAllOrganizations(): Observable<Organization[]> {
		const cacheKey = buildCacheKey("organizations", "all");

		return this.cacheService.get(
			cacheKey,
			() => {
				const organizationsRef = collection(this.firestore, this.collectionName);
				const organizationsQuery = query(organizationsRef, orderBy("name"));

				return new Observable<Organization[]>((subscriber) => {
					const unsubscribe = onSnapshot(
						organizationsQuery,
						(snapshot) => {
							const organizations = snapshot.docs.map((doc) => {
								const data = doc.data();
								// Map fields to ensure consistency
								return {
									id: doc.id,
									name: data["name"],
									description: data["description"],
									address: data["address"],
									city: data["city"],
									state: data["state"],
									country: data["country"],
									postal_code: data["postal_code"],
									phone: data["phone"],
									email: data["email"],
									website: data["website"],
									logo_url: data["logo_url"],
									active: data["active"] !== false, // Preserve actual active status
									created_at: data["created_at"],
									updated_at: data["updated_at"],
									status: data["status"],
									display_name: data["display_name"],
									vat_number: data["vat_number"],
									industry: data["industry"],
								} as Organization;
							});
							subscriber.next(organizations);
						},
						(error) => {
							subscriber.error(error);
						},
					);

					// Return cleanup function
					return () => unsubscribe();
				});
			},
			{ duration: 5 * 60 * 1000 }, // Cache for 5 minutes
		);
	}

	// Get organization by ID
	getOrganization(id: string): Observable<Organization | null> {
		const cacheKey = buildCacheKey("organizations", "single", id);

		return this.cacheService.get(
			cacheKey,
			() => {
				const organizationRef = doc(this.firestore, "organizations", id);
				return from(getDoc(organizationRef)).pipe(
					map((doc) => {
						if (!doc.exists()) return null;
						const data = doc.data();
						// Map fields to ensure consistency
						return {
							id: doc.id,
							name: data["name"],
							description: data["description"],
							address: data["address"],
							city: data["city"],
							state: data["state"],
							country: data["country"],
							postal_code: data["postal_code"],
							phone: data["phone"],
							email: data["email"],
							website: data["website"],
							logo_url: data["logo_url"],
							active: data["active"] !== false,
							created_at: data["created_at"],
							updated_at: data["updated_at"],
							status: data["status"],
							display_name: data["display_name"],
							vat_number: data["vat_number"],
							industry: data["industry"],
						} as Organization;
					}),
				);
			},
			{ duration: 5 * 60 * 1000 }, // Cache for 5 minutes
		);
	}

	// Check if organization name already exists
	private checkOrganizationNameExists(name: string): Observable<boolean> {
		const organizationsRef = collection(this.firestore, "organizations");
		const q = query(organizationsRef, where("name", "==", name), where("active", "==", true));

		return from(getDocs(q)).pipe(map((snapshot) => !snapshot.empty));
	}

	// Check if organization name already exists excluding a specific organization ID (for updates)
	private checkOrganizationNameExistsExcluding(
		name: string,
		excludeOrganizationId: string,
	): Observable<boolean> {
		const organizationsRef = collection(this.firestore, "organizations");
		const q = query(organizationsRef, where("name", "==", name), where("active", "==", true));

		return from(getDocs(q)).pipe(
			map((snapshot) => {
				// Check if any organization with this name exists that is not the excluded organization
				return snapshot.docs.some((doc) => doc.id !== excludeOrganizationId);
			}),
		);
	}

	// Create new organization
	createOrganization(data: CreateOrganizationData): Observable<string> {
		return this.authService.user$.pipe(
			switchMap((user) => {
				if (!user) {
					return throwError(() => new Error("User not authenticated"));
				}

				// Check if organization name already exists
				return this.checkOrganizationNameExists(data.name).pipe(
					switchMap((nameExists) => {
						if (nameExists) {
							return throwError(
								() => new Error("An organization with this name already exists"),
							);
						}

						const organizationData = {
							...data,
							created_at: Timestamp.now(),
							created_by: user.uid,
							updated_at: Timestamp.now(),
							updated_by: user.uid,
							active: true,
							status: "active",
						};

						const organizationsRef = collection(this.firestore, "organizations");
						const newDocRef = doc(organizationsRef);

						return from(setDoc(newDocRef, organizationData)).pipe(
							map(() => {
								// Clear cache after creating organization
								this.cacheService.clearPattern("organizations");
								this.cacheService.clearPattern("dashboard");
								return newDocRef.id;
							}),
							catchError((error) => {
								console.error("Error creating organization:", error);
								return throwError(() => error);
							}),
						);
					}),
				);
			}),
			catchError((error) => {
				console.error("Error in createOrganization stream:", error);
				return throwError(() => error);
			}),
		);
	}

	// Update organization
	updateOrganization(id: string, data: UpdateOrganizationData): Observable<void> {
		return this.authService.user$.pipe(
			switchMap((user) => {
				if (!user) {
					throw new Error("User not authenticated");
				}

				// If name is being updated, check for uniqueness
				if (data.name) {
					return this.checkOrganizationNameExistsExcluding(data.name, id).pipe(
						switchMap((nameExists) => {
							if (nameExists) {
								throw new Error("An organization with this name already exists");
							}

							const organizationRef = doc(this.firestore, "organizations", id);
							const updateData = {
								...data,
								updated_at: Timestamp.now(),
								updated_by: user.uid,
							};
							return from(updateDoc(organizationRef, updateData)).pipe(
								map(() => {
									// Clear cache after updating organization
									this.cacheService.remove(
										buildCacheKey("organizations", "single", id),
									);
									this.cacheService.clearPattern("organizations");
									this.cacheService.clearPattern("dashboard");
								}),
							);
						}),
					);
				}

				// If name is not being updated, proceed normally
				const organizationRef = doc(this.firestore, "organizations", id);
				const updateData = {
					...data,
					updated_at: Timestamp.now(),
					updated_by: user.uid,
				};
				return from(updateDoc(organizationRef, updateData)).pipe(
					map(() => {
						// Clear cache after updating organization
						this.cacheService.remove(buildCacheKey("organizations", "single", id));
						this.cacheService.clearPattern("organizations");
						this.cacheService.clearPattern("dashboard");
					}),
				);
			}),
		);
	}

	// Get the current organization context
	getCurrentOrganization(): Observable<Organization | null> {
		return this.currentOrganization$;
	}

	// Set the current organization context
	setCurrentOrganization(organization: Organization | null): void {
		this.currentOrganizationSubject.next(organization);

		// Save to localStorage for persistence
		if (organization && organization.id) {
			localStorage.setItem("currentOrganizationId", organization.id);
		} else {
			localStorage.removeItem("currentOrganizationId");
		}
	}

	// Load the current organization from localStorage
	private async loadCurrentOrganizationFromStorage(): Promise<void> {
		const orgId = localStorage.getItem("currentOrganizationId");
		if (orgId) {
			const org = await firstValueFrom(this.getOrganization(orgId));
			if (org) {
				this.currentOrganizationSubject.next(org);
			}
		}
	}

	// Add organization respondent.
	//
	// sendWelcomeEmail is required (issue #64). Respondent dialogs default it
	// off — the survey invitation is the email that matters — while the org
	// admin paths pass true, since that link is the admin's only way in.
	addOrganizationRespondent(
		organizationId: string,
		userData: Partial<UserWithRoles>,
		sendWelcomeEmail: boolean,
	): Observable<string> {
		if (!userData.email) {
			return throwError(() => new Error("Email is required"));
		}

		return from(this.checkUserExists(userData.email)).pipe(
			switchMap((existingUser) => {
				if (existingUser) {
					// Create respondent association with correct ID pattern
					const associationId = `${organizationId}_${existingUser.id}`;
					const respondentData: OrganizationMembers = {
						user_id: existingUser.id!,
						organization_id: organizationId,
						roles: (userData.roles as UserRoles) || { respondent: true },
						active: true,
						created_at: new Date(),
						updated_at: new Date(),
					};
					return from(
						setDoc(
							doc(this.firestore, "organizationUsers", associationId),
							respondentData,
						),
					).pipe(
						map(() => {
							this.clearMemberCaches(organizationId);
							return associationId;
						}),
					);
				} else {
					// Create new user
					return this.userService
						.createUser(
							{
								...userData,
								first_name: userData.first_name || "",
								last_name: userData.last_name || "",
								roles: (userData.roles as UserRoles) || {
									respondent: true,
								},
								active: true,
							} as UserWithRoles,
							"organizations",
							sendWelcomeEmail,
						)
						.pipe(
							switchMap((uid) => {
								// Create respondent association with correct ID pattern
								const associationId = `${organizationId}_${uid}`;
								const respondentData: OrganizationMembers = {
									user_id: uid,
									organization_id: organizationId,
									roles: (userData.roles as UserRoles) || {
										respondent: true,
									},
									active: true,
									created_at: new Date(),
									updated_at: new Date(),
								};
								return from(
									setDoc(
										doc(this.firestore, "organizationUsers", associationId),
										respondentData,
									),
								).pipe(
									map(() => {
										this.clearMemberCaches(organizationId);
										return associationId;
									}),
								);
							}),
						);
				}
			}),
		);
	}

	// Check if user exists
	private checkUserExists(email: string): Promise<UserWithRoles | null> {
		const usersRef = collection(this.firestore, "users");
		const q = query(usersRef, where("email", "==", normalizeEmail(email)));
		return getDocs(q).then((snapshot) => {
			if (!snapshot.empty) {
				const doc = snapshot.docs[0];
				return { id: doc.id, ...doc.data() } as UserWithRoles;
			}
			return null;
		});
	}

	// Drop every cached view that a write to this organization can invalidate
	private clearOrganizationCaches(organizationId: string): void {
		this.cacheService.remove(buildCacheKey("organizations", "single", organizationId));
		this.cacheService.clearPattern("organizations");
		this.cacheService.clearPattern("dashboard");
	}

	// Membership writes also touch the user documents behind the global user lists
	private clearMemberCaches(organizationId: string): void {
		this.clearOrganizationCaches(organizationId);
		this.cacheService.clearPattern("users");
	}

	// Get respondents in organization
	/**
	 * An association whose user_id is not a string cannot address a
	 * users/{uid} document: doc() throws on it, and because the reads run
	 * under Promise.all that one row used to take the entire organisation's
	 * list with it. Such rows are dropped from the result and counted here
	 * instead, so the records read as absent and the page can say how many
	 * are missing. Nothing is substituted for them, and the defect stays
	 * visible rather than silently shrinking the list.
	 */
	private readonly unreadable = signal<Record<string, number>>({});

	unreadableMembers(organizationId: string): number {
		return this.unreadable()[organizationId] ?? 0;
	}

	private usableAssociations<T extends { id: string; userId: unknown }>(
		organizationId: string,
		associations: T[],
	): (T & { userId: string })[] {
		const usable: (T & { userId: string })[] = [];
		const broken: string[] = [];
		for (const association of associations) {
			if (typeof association.userId === "string" && association.userId.length > 0) {
				usable.push(association as T & { userId: string });
			} else {
				broken.push(association.id);
			}
		}

		if (broken.length > 0) {
			console.error(
				`[organizations] ${broken.length} association(s) in ${organizationId} have an unusable user_id`,
				broken,
			);
		}
		this.unreadable.update((counts) => ({ ...counts, [organizationId]: broken.length }));
		return usable;
	}

	/**
	 * The users behind a set of associations, read in one batched query per
	 * thirty ids rather than one getDoc per member. Associations whose user
	 * document is missing are dropped. Roles always come from the association;
	 * `active` comes from the association when `activeFromAssociation` is set
	 * (lists that include archived members) and from the user otherwise.
	 */
	private async usersForAssociations(
		organizationId: string,
		associations: Array<{
			id: string;
			userId: unknown;
			roles: UserWithRoles["roles"];
			active?: boolean;
		}>,
		activeFromAssociation: boolean,
	): Promise<UserWithRoles[]> {
		const usable = this.usableAssociations(organizationId, associations);
		const users = await fetchDocsByIds(
			this.firestore,
			"users",
			usable.map((association) => association.userId),
		);
		return usable.flatMap((association) => {
			const userData = users.get(association.userId);
			if (!userData) return [];
			return [
				{
					id: association.userId,
					uid: association.userId,
					email: userData["email"],
					displayName: deriveDisplayName(
						userData["first_name"],
						userData["last_name"],
						userData["email"],
					),
					first_name: userData["first_name"] || "",
					last_name: userData["last_name"] || "",
					photo_url: userData["photo_url"],
					active: activeFromAssociation
						? association.active !== false
						: userData["active"] !== false,
					lastSignInAt: userData["last_sign_in_at"],
					email_verified: userData["email_verified"],
					roles: association.roles,
				} as UserWithRoles,
			];
		});
	}

	private hasRole(roles: UserWithRoles["roles"], role: string): boolean {
		if (Array.isArray(roles)) return roles.includes(role);
		return roles?.[role as keyof typeof roles] === true;
	}

	getOrganizationRespondents(organizationId: string): Observable<UserWithRoles[]> {
		const associationsRef = collection(this.firestore, "organizationUsers");
		const q = query(
			associationsRef,
			where("organization_id", "==", organizationId),
			where("active", "==", true),
		);

		return from(getDocs(q)).pipe(
			switchMap((snapshot) => {
				if (snapshot.empty) return of([]);
				const associations = snapshot.docs.map((doc) => ({
					id: doc.id,
					userId: doc.data()["user_id"],
					roles: doc.data()["roles"],
				}));
				return from(this.usersForAssociations(organizationId, associations, false));
			}),
			map((users) => users.filter((user) => this.hasRole(user.roles, "respondent"))),
		);
	}

	// Get admins in organization (users with org_admin role)
	getOrganizationAdmins(organizationId: string): Observable<UserWithRoles[]> {
		const associationsRef = collection(this.firestore, "organizationUsers");
		const q = query(
			associationsRef,
			where("organization_id", "==", organizationId),
			where("active", "==", true),
		);

		return from(getDocs(q)).pipe(
			switchMap((snapshot) => {
				if (snapshot.empty) return of([]);
				const adminAssociations = snapshot.docs
					.map((doc) => ({
						id: doc.id,
						userId: doc.data()["user_id"],
						roles: doc.data()["roles"],
					}))
					.filter((a) => this.hasRole(a.roles, "org_admin"));
				if (adminAssociations.length === 0) return of([]);
				return from(this.usersForAssociations(organizationId, adminAssociations, false));
			}),
		);
	}

	// Get all respondents in organization (active and archived)
	getAllOrganizationRespondents(organizationId: string): Observable<UserWithRoles[]> {
		const associationsRef = collection(this.firestore, "organizationUsers");
		const q = query(associationsRef, where("organization_id", "==", organizationId));

		return from(getDocs(q)).pipe(
			switchMap((snapshot) => {
				if (snapshot.empty) return of([]);
				const associations = snapshot.docs.map((doc) => ({
					id: doc.id,
					userId: doc.data()["user_id"],
					roles: doc.data()["roles"],
					active: doc.data()["active"] !== false,
				}));
				return from(this.usersForAssociations(organizationId, associations, true));
			}),
			map((users) => users.filter((user) => this.hasRole(user.roles, "respondent"))),
		);
	}

	// Update organization respondent
	updateOrganizationRespondent(
		organizationId: string,
		respondentId: string,
		updateData: {
			first_name?: string;
			last_name?: string;
			roles?: Record<string, boolean>;
			active?: boolean;
		},
	): Observable<void> {
		const associationRef = doc(
			this.firestore,
			"organizationUsers",
			`${organizationId}_${respondentId}`,
		);
		const userRef = doc(this.firestore, "users", respondentId);

		const associationUpdate: any = {
			updated_at: Timestamp.now(),
		};

		const userUpdate: any = {
			updated_at: Timestamp.now(),
		};

		if (updateData.roles) {
			associationUpdate.roles = updateData.roles;
		}

		if (updateData.active !== undefined) {
			associationUpdate.active = updateData.active;
		}

		// Update user document with first_name and last_name
		if (updateData.first_name !== undefined) {
			userUpdate.first_name = updateData.first_name;
		}

		if (updateData.last_name !== undefined) {
			userUpdate.last_name = updateData.last_name;
		}

		// Update both documents
		return from(
			Promise.all([
				updateDoc(associationRef, associationUpdate),
				updateDoc(userRef, userUpdate),
			]),
		).pipe(
			map(() => {
				this.clearMemberCaches(organizationId);
				this.cacheService.remove(buildCacheKey("users", "single", respondentId));
			}),
		);
	}

	// Remove organization respondent
	removeOrganizationRespondent(organizationId: string, respondentId: string): Observable<void> {
		return from(
			updateDoc(
				doc(this.firestore, "organizationUsers", `${organizationId}_${respondentId}`),
				{
					active: false,
					updated_at: serverTimestamp(),
				},
			),
		).pipe(map(() => this.clearMemberCaches(organizationId)));
	}

	// Add survey to organization
	addSurveyToOrganization(organizationId: string, surveyId: string): Observable<void> {
		const associationId = `${organizationId}_${surveyId}`;
		const associationRef = doc(this.firestore, "organizationSurveys", associationId);

		const associationData = {
			active: true,
			survey_id: surveyId, // Use snake_case for consistency
			organization_id: organizationId,
			created_at: Timestamp.now(),
			updated_at: Timestamp.now(),
		};

		// Using setDoc with merge: false ensures we don't create duplicates
		return from(setDoc(associationRef, associationData, { merge: false })).pipe(
			map(() => this.clearOrganizationCaches(organizationId)),
			catchError((error) => {
				if (error.code === "already-exists") {
					return of(void 0);
				}
				throw error;
			}),
		);
	}

	// Remove survey from organization
	removeSurveyFromOrganization(organizationId: string, surveyId: string): Observable<void> {
		return from(
			(async (): Promise<void> => {
				const batch = writeBatch(this.firestore);

				// 1. Update the organization survey association
				const associationId = `${organizationId}_${surveyId}`;
				const associationRef = doc(this.firestore, "organizationSurveys", associationId);
				batch.update(associationRef, {
					active: false,
					updated_at: Timestamp.now(),
				});

				// 2. Update all member surveys for this organization and survey
				const respondentSurveysRef = collection(this.firestore, "respondentSurveys");
				const respondentSurveysQuery = query(
					respondentSurveysRef,
					where("organization_id", "==", organizationId),
					where("survey_id", "==", surveyId),
					where("active", "==", true),
				);

				const respondentSurveysSnapshot = await getDocs(respondentSurveysQuery);

				respondentSurveysSnapshot.docs.forEach((respondentSurveyDoc) => {
					batch.update(respondentSurveyDoc.ref, {
						active: false,
						updated_at: Timestamp.now(),
					});
				});

				// Execute the batch
				await batch.commit();
			})(),
		).pipe(map(() => this.clearOrganizationCaches(organizationId)));
	}

	// Update organization survey association
	updateOrganizationSurveyAssociation(
		organizationId: string,
		surveyId: string,
		data: { active: boolean },
	): Observable<void> {
		// Update the survey document's active field directly
		const surveyRef = doc(this.firestore, "surveys", surveyId);

		const surveyUpdate: any = {
			active: data.active,
			updated_at: Timestamp.now(),
		};

		return from(updateDoc(surveyRef, surveyUpdate)).pipe(
			map(() => this.clearOrganizationCaches(organizationId)),
		);
	}

	// Get surveys in organization
	getOrganizationSurveys(organizationId: string): Observable<Survey[]> {
		// Get surveys from two sources:
		// 1. organizationSurveys collection (for published surveys)
		// 2. surveys collection directly (for drafts that might not be associated yet)

		const associationsRef = collection(this.firestore, "organizationSurveys");
		const surveysRef = collection(this.firestore, "surveys");

		// Query 1: Get associated surveys
		const associationsQuery = query(
			associationsRef,
			where("organization_id", "==", organizationId),
		);

		// Query 2: Get all surveys that include this org in organization_ids
		const directSurveysQuery = query(
			surveysRef,
			where("organization_ids", "array-contains", organizationId),
		);

		return combineLatest([
			from(getDocs(associationsQuery)),
			from(getDocs(directSurveysQuery)),
		]).pipe(
			switchMap(([associationsSnapshot, directSurveysSnapshot]) => {
				// Get survey IDs from associations
				const associatedSurveyIds = associationsSnapshot.docs
					.map((doc) => {
						const data = doc.data();
						return data["survey_id"];
					})
					.filter((id) => id); // Filter out any undefined values

				// For direct surveys, we already have the data
				const directSurveys = directSurveysSnapshot.docs.map((doc) => {
					const survey = this.normalizeSurvey({
						id: doc.id,
						...doc.data(),
					});
					return survey;
				});

				// For associated surveys, we need to fetch them
				if (associatedSurveyIds.length === 0) {
					return of(directSurveys);
				}

				const uniqueAssociatedIds = [...new Set(associatedSurveyIds)];

				// Fetch associated survey documents in batches
				return from(
					fetchDocsByIds(this.firestore, "surveys", uniqueAssociatedIds as string[]).then(
						(docs) =>
							Array.from(docs, ([id, data]) => this.normalizeSurvey({ id, ...data })),
					),
				).pipe(
					map((associatedSurveys) => {
						const filteredAssociated = associatedSurveys.filter(
							(survey): survey is Survey => survey !== null,
						);

						// Combine direct and associated surveys
						const allSurveys = [...directSurveys, ...filteredAssociated];

						// Remove any duplicates (in case a survey is in both collections)
						const uniqueSurveys = allSurveys.filter(
							(survey, index, arr) =>
								arr.findIndex((c) => c.id === survey.id) === index,
						);

						// Sort by status in the order: draft, ready, active, completed
						const statusOrder = {
							draft: 0,
							ready: 1,
							active: 2,
							completed: 3,
						};

						return uniqueSurveys.sort((a, b) => {
							const orderA = statusOrder[a.status] ?? 99;
							const orderB = statusOrder[b.status] ?? 99;
							return orderA - orderB;
						});
					}),
				);
			}),
		);
	}

	// Get all surveys in organization (active and inactive)
	getAllOrganizationSurveys(organizationId: string): Observable<Survey[]> {
		// Get surveys from two sources:
		// 1. organizationSurveys collection (for published surveys)
		// 2. surveys collection directly (for drafts that might not be associated yet)

		const associationsRef = collection(this.firestore, "organizationSurveys");
		const surveysRef = collection(this.firestore, "surveys");

		// Query 1: Get all associated surveys (active and inactive)
		const associationsQuery = query(
			associationsRef,
			where("organization_id", "==", organizationId),
		);

		// Query 2: Get all surveys that include this org in organization_ids
		const directSurveysQuery = query(
			surveysRef,
			where("organization_ids", "array-contains", organizationId),
		);

		return combineLatest([
			from(getDocs(associationsQuery)),
			from(getDocs(directSurveysQuery)),
		]).pipe(
			switchMap(([associationsSnapshot, directSurveysSnapshot]) => {
				// For direct surveys, we already have the data
				const directSurveys = directSurveysSnapshot.docs.map((doc) => {
					const survey = this.normalizeSurvey({
						id: doc.id,
						...doc.data(),
					});
					// Use the survey's active field directly
					survey.active = doc.data()["active"] !== false;

					return survey;
				});

				// For associated surveys, we need to fetch them
				const associatedSurveyIds = associationsSnapshot.docs
					.map((doc) => {
						const data = doc.data();
						return data["survey_id"];
					})
					.filter((id) => id); // Filter out any undefined values

				if (associatedSurveyIds.length === 0) {
					return of(directSurveys);
				}

				const uniqueAssociatedIds = [...new Set(associatedSurveyIds)];

				// Fetch associated survey documents in batches
				return from(
					fetchDocsByIds(this.firestore, "surveys", uniqueAssociatedIds).then((docs) =>
						Array.from(docs, ([id, data]) =>
							this.normalizeSurvey({
								id,
								...data,
								// Use the survey's active field directly
								active: data["active"] !== false,
							}),
						),
					),
				).pipe(
					map((associatedSurveys) => {
						const filteredAssociated = associatedSurveys.filter(
							(survey): survey is Survey => survey !== null,
						);

						// Combine direct and associated surveys
						const allSurveys = [...directSurveys, ...filteredAssociated];

						// Remove any duplicates (in case a survey is in both collections)
						const uniqueSurveys = allSurveys.filter(
							(survey, index, arr) =>
								arr.findIndex((c) => c.id === survey.id) === index,
						);

						// Sort by status in the order: draft, ready, active, completed
						const statusOrder = {
							draft: 0,
							ready: 1,
							active: 2,
							completed: 3,
						};

						return uniqueSurveys.sort((a, b) => {
							const orderA = statusOrder[a.status] ?? 99;
							const orderB = statusOrder[b.status] ?? 99;
							return orderA - orderB;
						});
					}),
				);
			}),
		);
	}

	// Delete organization
	deleteOrganization(organizationId: string, hardDelete: boolean = false): Observable<void> {
		return this.authService.user$.pipe(
			switchMap((user) => {
				if (!user) {
					console.error("No authenticated user found");
					return throwError(() => new Error("User not authenticated"));
				}

				if (hardDelete) {
					// Hard delete - remove the document completely
					const batch = writeBatch(this.firestore);
					const organizationRef = doc(this.firestore, "organizations", organizationId);

					// Delete the organization document
					batch.delete(organizationRef);

					// Return a promise that resolves when all operations are complete
					return from(batch.commit()).pipe(
						map(() => {
							// Clear cache after deleting organization
							this.cacheService.remove(
								buildCacheKey("organizations", "single", organizationId),
							);
							this.cacheService.clearPattern("organizations");
							this.cacheService.clearPattern("dashboard");
						}),
					);
				} else {
					// Soft delete - just update the active status
					const organizationRef = doc(this.firestore, "organizations", organizationId);
					return from(
						updateDoc(organizationRef, {
							active: false,
							updated_at: serverTimestamp(),
							updated_by: user.uid,
						}),
					).pipe(
						map(() => {
							// Clear cache after deleting organization
							this.cacheService.remove(
								buildCacheKey("organizations", "single", organizationId),
							);
							this.cacheService.clearPattern("organizations");
							this.cacheService.clearPattern("dashboard");
						}),
					);
				}
			}),
			catchError((error) => {
				console.error("Error in deleteOrganization:", error);
				return throwError(() => error);
			}),
		);
	}

	// Get organizations for a specific user
	getOrganizationsForUser(userId: string): Observable<Organization[]> {
		// First get all organization member associations for this user
		const memberAssociationsRef = collection(this.firestore, "organizationUsers");
		const memberAssociationsQuery = query(
			memberAssociationsRef,
			where("user_id", "==", userId),
			where("active", "==", true),
		);

		return from(getDocs(memberAssociationsQuery)).pipe(
			switchMap((snapshot) => {
				if (snapshot.empty) {
					return of([]);
				}

				// Get all organization IDs the user has access to
				const organizationIds = snapshot.docs.map((doc) => doc.data()["organization_id"]);

				// Get all organization documents for these IDs, in batches
				return from(
					fetchDocsByIds(this.firestore, "organizations", organizationIds).then((docs) =>
						Array.from(docs)
							.filter(([, data]) => data["active"])
							.map(([id, data]) => ({ id, ...data }) as Organization),
					),
				);
			}),
		);
	}

	// Map each user id to the name of the first (earliest-joined) active
	// organization they belong to. Used by the global All Users list.
	getFirstOrganizationNameByUser(): Observable<Record<string, string>> {
		const memberAssociationsRef = collection(this.firestore, "organizationUsers");
		const memberAssociationsQuery = query(memberAssociationsRef, where("active", "==", true));

		return from(getDocs(memberAssociationsQuery)).pipe(
			switchMap((snapshot) => {
				if (snapshot.empty) {
					return of({} as Record<string, string>);
				}

				// Keep the earliest active membership per user (by created_at).
				const firstByUser = new Map<
					string,
					{ organizationId: string; createdAt: number }
				>();
				snapshot.docs.forEach((memberDoc) => {
					const data = memberDoc.data();
					const userId = data["user_id"];
					const organizationId = data["organization_id"];
					if (!userId || !organizationId) {
						return;
					}
					const createdAt = data["created_at"]?.toMillis?.() ?? 0;
					const existing = firstByUser.get(userId);
					if (!existing || createdAt < existing.createdAt) {
						firstByUser.set(userId, { organizationId, createdAt });
					}
				});

				const organizationIds = Array.from(
					new Set(Array.from(firstByUser.values()).map((entry) => entry.organizationId)),
				);

				return from(fetchDocsByIds(this.firestore, "organizations", organizationIds)).pipe(
					map((docs) => {
						const nameById = new Map(
							Array.from(docs, ([id, data]) => [id, data["name"] as string]),
						);
						const namesByUser: Record<string, string> = {};
						firstByUser.forEach((entry, userId) => {
							const name = nameById.get(entry.organizationId);
							if (name) {
								namesByUser[userId] = name;
							}
						});
						return namesByUser;
					}),
				);
			}),
		);
	}

	// Get all organizations (active and inactive) for a specific user
	getAllOrganizationsForUser(userId: string): Observable<Organization[]> {
		const memberAssociationsRef = collection(this.firestore, "organizationUsers");
		const memberAssociationsQuery = query(
			memberAssociationsRef,
			where("user_id", "==", userId),
		);

		return from(getDocs(memberAssociationsQuery)).pipe(
			switchMap((snapshot) => {
				if (snapshot.empty) {
					return of([]);
				}

				const organizationIds = snapshot.docs.map((doc) => doc.data()["organization_id"]);

				return from(
					fetchDocsByIds(this.firestore, "organizations", organizationIds).then((docs) =>
						Array.from(docs, ([id, data]) => ({ id, ...data }) as Organization),
					),
				);
			}),
		);
	}
}
